import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Heart, Shuffle, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SectionHeading } from '../components/SectionHeading';
import { birthdayConfig } from '../config';
import { assetUrl } from '../data/assets';

function MemoryPhoto({
  src,
  alt,
  className,
  loading = 'lazy',
}: {
  src: string;
  alt: string;
  className: string;
  loading?: 'eager' | 'lazy';
}) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className={`${className} memory-photo-error`} role="img" aria-label={`${alt} could not be loaded`}>
        <span>Photo could not be loaded</span>
      </div>
    );
  }

  return (
    <img
      src={assetUrl(src)}
      alt={alt}
      className={className}
      loading={loading}
      decoding="async"
      draggable={false}
      onError={() => setFailed(true)}
    />
  );
}

export function MemoriesSection() {
  const [order, setOrder] = useState(() => birthdayConfig.memories.map((_, index) => index));
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const touchStart = useRef<number | null>(null);
  const orderedMemories = useMemo(() => order.map((index) => birthdayConfig.memories[index]), [order]);

  const close = () => setActiveIndex(null);
  const previous = () =>
    setActiveIndex((current) =>
      current === null ? 0 : (current - 1 + orderedMemories.length) % orderedMemories.length,
    );
  const next = () =>
    setActiveIndex((current) =>
      current === null ? 0 : (current + 1) % orderedMemories.length,
    );

  useEffect(() => {
    if (activeIndex === null) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') previous();
      if (event.key === 'ArrowRight') next();
    };

    document.addEventListener('keydown', onKey);
    document.body.classList.add('modal-open');

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('modal-open');
    };
  }, [activeIndex, orderedMemories.length]);

  const shuffle = () => {
    setOrder((current) => {
      const nextOrder = [...current];
      for (let index = nextOrder.length - 1; index > 0; index -= 1) {
        const random = Math.floor(Math.random() * (index + 1));
        [nextOrder[index], nextOrder[random]] = [nextOrder[random], nextOrder[index]];
      }
      return nextOrder;
    });
  };

  return (
    <section id="memories" className="section-shell memories-section">
      <div className="page-container">
        <SectionHeading
          eyebrow={birthdayConfig.copy.memories.eyebrow}
          title={birthdayConfig.copy.memories.title}
          description={birthdayConfig.copy.memories.description}
        />

        <div className="section-action-row">
          <button className="secondary-button" onClick={shuffle}>
            <Shuffle size={18} /> {birthdayConfig.copy.memories.shuffleButton}
          </button>
        </div>

        <motion.div className="memory-grid" layout>
          {orderedMemories.map((memory, index) => (
            <motion.button
              key={memory.title}
              type="button"
              className="memory-card"
              layout
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ delay: index * 0.07 }}
              whileHover={{ y: -6 }}
              onClick={() => setActiveIndex(index)}
            >
              <div className="memory-image-wrap">
                <MemoryPhoto
                  src={memory.image}
                  alt={memory.title}
                  className="memory-image"
                  loading={index < 3 ? 'eager' : 'lazy'}
                />
                <span className="memory-heart"><Heart size={18} fill="currentColor" /></span>
                <img
                  src={assetUrl(memory.sticker)}
                  alt=""
                  aria-hidden="true"
                  className="memory-sticker"
                  loading="lazy"
                  draggable={false}
                />
              </div>

              <div className="memory-copy">
                <span>{memory.date}</span>
                <h3>{memory.title}</h3>
                <p>{memory.message}</p>
              </div>
            </motion.button>
          ))}
        </motion.div>

        <div className="carousel-indicators" aria-hidden="true">
          {orderedMemories.map((_, index) => <span key={index} />)}
        </div>
      </div>

      <AnimatePresence>
        {activeIndex !== null && (
          <motion.div className="memory-viewer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <button className="icon-button viewer-close" onClick={close} aria-label="Close image viewer"><X /></button>
            <button className="icon-button viewer-nav viewer-prev" onClick={previous} aria-label="Previous memory"><ChevronLeft /></button>

            <motion.div
              className="viewer-content"
              key={activeIndex}
              initial={{ opacity: 0, x: 30, scale: 0.97 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              onTouchStart={(event) => { touchStart.current = event.touches[0]?.clientX ?? null; }}
              onTouchEnd={(event) => {
                if (touchStart.current === null) return;
                const delta = (event.changedTouches[0]?.clientX ?? touchStart.current) - touchStart.current;
                if (delta > 45) previous();
                if (delta < -45) next();
                touchStart.current = null;
              }}
            >
              <MemoryPhoto
                src={orderedMemories[activeIndex].image}
                alt={orderedMemories[activeIndex].title}
                className="viewer-image"
                loading="eager"
              />

              <div className="viewer-copy">
                <span>{orderedMemories[activeIndex].date}</span>
                <h3>{orderedMemories[activeIndex].title}</h3>
                <p>{orderedMemories[activeIndex].message}</p>
              </div>
            </motion.div>

            <button className="icon-button viewer-nav viewer-next" onClick={next} aria-label="Next memory"><ChevronRight /></button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}